var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * Represents a client for making GraphQL requests.
 */
var GraphQLClient = /** @class */ (function () {
    function GraphQLClient(configuration) {
        var _this = this;
        this.GetPendingRequests = function () { return _this.pendingRequests; };
        this.GetActiveSubscriptions = function () { return _this.activeSubscriptions; };
        this.ExecuteQueryRaw = function (request) {
            var body;
            // If the request should be sent as a form, create a FormData object and append the necessary fields
            if (_this.asForm) {
                var formData = new FormData();
                formData.append("query", request.query);
                if (request.variables)
                    formData.append("variables", JSON.stringify(request.variables));
                if (request.operationName)
                    formData.append("operationName", request.operationName);
                if (request.extensions)
                    formData.append("extensions", JSON.stringify(request.extensions));
                body = formData;
            }
            else { // Otherwise, send the request as a JSON string
                body = JSON.stringify(request);
            }
            // Create a cancel source using the AbortController API, if it is available
            var cancelSource = typeof AbortController === "undefined" ? undefined : new AbortController();
            // Create a new IRequest object with the URL, method, body, headers, and cancel signal
            var config = {
                url: _this.url,
                method: "POST",
                body: body,
                headers: _this.asForm ? undefined : { "Content-Type": "application/json" },
                signal: cancelSource ? cancelSource.signal : null,
            };
            // Increment the number of pending requests
            _this.pendingRequests += 1;
            // If a transformRequest function is defined, apply it to the request configuration
            var configPromise = _this.transformRequest ? Promise.resolve(_this.transformRequest(config)) : Promise.resolve(config);
            // Send the request using fetch, and return a promise that resolves to the response
            var ret = configPromise
                .then(function (config2) {
                // Create the Request object
                var config3 = __assign({}, config2);
                var url = config3.url;
                delete config3.url;
                return new Request(url, config3);
            })
                .then(function (request) {
                // Start the Fetch operation
                return fetch(request).then(function (data) {
                    // Decrement the number of pending requests
                    _this.pendingRequests -= 1;
                    // Check if the response status is valid (between 200 and 299 or between 400 and 499)
                    var valid = (data.status >= 200 && data.status < 300) ||
                        (data.status >= 400 && data.status < 500);
                    // If the response status is not valid, create a new query result object with the error message
                    if (!valid) {
                        var queryRet = {
                            networkError: true,
                            errors: [{ message: data.statusText }],
                            size: 1000,
                        };
                        return Promise.resolve(queryRet);
                    }
                    // Parse the JSON data and create a new query result object with the data and any errors
                    return data.json().then(function (jsonData) {
                        var queryRet = jsonData;
                        if (queryRet.errors && queryRet.errors.length)
                            queryRet.data = undefined;
                        queryRet.networkError = false;
                        // Calculate the size of the response and set it on the query result object
                        var contentLengthHeader = data.headers.get('Content-Length');
                        if (contentLengthHeader) {
                            queryRet.size = parseInt(contentLengthHeader, 10);
                        }
                        else {
                            queryRet.size = JSON.stringify(jsonData).length;
                        }
                        return Promise.resolve(queryRet);
                    });
                })
                    .catch(function (error) {
                    // If an error occurs, create a new query result object with the error message and the underlying error
                    var queryRet = {
                        networkError: true,
                        errors: [{ message: (typeof (error) === "string") ? error : (error.message || 'Unknown error from Fetch API') }],
                        extensions: {
                            underlyingError: error,
                        },
                        size: 1000,
                    };
                    return Promise.resolve(queryRet);
                });
            }, 
            // If an error occurs while creating the request configuration, create a new query result object with the error message and the underlying error
            function (error) {
                _this.pendingRequests -= 1;
                var queryRet = {
                    networkError: true,
                    errors: [{ message: (typeof (error) === "string") ? error : 'Error initializing request configuration' }],
                    extensions: {
                        underlyingError: error,
                    },
                    size: 1000,
                };
                return Promise.resolve(queryRet);
            });
            // Return an object with the result promise and an abort function that cancels the request
            var doAbort = (cancelSource === null || cancelSource === void 0 ? void 0 : cancelSource.abort) || (function () { });
            return {
                result: ret,
                abort: doAbort,
            };
        };
        this.ExecuteQuery = function (request, cacheMode, cacheTimeout) {
            // Set cache mode based on input or default cache policy
            cacheMode = cacheMode || _this.defaultCachePolicy;
            // Set cache timeout based on input or default cache time
            var cacheTimeoutValue = cacheTimeout !== undefined ? cacheTimeout : _this.defaultCacheTime;
            // Stringify the request object
            var queryAndVariablesString = JSON.stringify(request);
            // A factory function for creating new cache entries
            var newEntryFactory = function (newEntry) {
                // Define the response object for the cache entry
                newEntry.response = {
                    result: null,
                    resultPromise: null,
                    loading: false,
                    // Refresh function for executing the query and updating the cache entry
                    refresh: function () {
                        // If already loading, return the promise representing the currently-executing request
                        if (newEntry.response.loading)
                            return newEntry.response.resultPromise;
                        // Otherwise, start executing the request
                        newEntry.response.loading = true;
                        var exec = _this.ExecuteQueryRaw(request);
                        newEntry.response.resultPromise = exec.result;
                        newEntry.cancelRequest = exec.abort;
                        // When the query result is resolved (note: data may contain a sucessful or failed response; the promise will not be rejected)
                        exec.result.then(function (data) {
                            // Check if the query has been force refreshed then discard the original result; otherwise:
                            if (newEntry.response.resultPromise === exec.result) {
                                // Update the cache entry and notify subscribers
                                newEntry.response.result = data;
                                newEntry.response.loading = false;
                                newEntry.cancelRequest = undefined;
                                if (data.errors && data.errors.length) {
                                    // If there were any errors, immediately expire the cache entry
                                    newEntry.expires = 0;
                                }
                                else {
                                    // Set the cache entry size
                                    _this.SetCacheEntrySize(newEntry, data.size + 1000);
                                    // Set the cache entry expiration date
                                    if (cacheMode !== "no-cache")
                                        newEntry.expires = Date.now() + cacheTimeoutValue;
                                }
                                // Notify all subscribers of the new data
                                newEntry.subscribers.forEach(function (subscriber) {
                                    subscriber(data);
                                });
                            }
                        });
                        // Return a promise that resolves when the GraphQL operation is complete
                        // (note: data may contain a sucessful or failed response; the promise will not be rejected)
                        return exec.result;
                    },
                    // Function to force refresh the cache entry
                    forceRefresh: function () {
                        // If in the process of loading, terminate the request, discard the results and retry
                        if (newEntry.response.loading) {
                            if (newEntry.cancelRequest) {
                                newEntry.cancelRequest();
                            }
                            newEntry.cancelRequest = undefined;
                            newEntry.response.loading = false;
                            newEntry.response.resultPromise = null;
                            // No need to notify subscribers because it's still loading anyway; no data would have been sent
                        }
                        // Execute the GraphQL request again
                        newEntry.response.refresh();
                    },
                    // Function to clear and refresh the cache entry
                    clearAndRefresh: function () {
                        // Perform force-refresh
                        newEntry.response.forceRefresh();
                        // Push out null result to subscribers if anything was sent out already
                        if (newEntry.response.result !== null) {
                            newEntry.response.result = null;
                            newEntry.subscribers.forEach(function (subscriber) {
                                subscriber(null);
                            });
                        }
                    },
                    // Function to subscribe to cache entry updates
                    subscribe: function (callback) {
                        newEntry.subscribers.push(callback);
                        return function () {
                            newEntry.subscribers = newEntry.subscribers.filter(function (x) { return x !== callback; });
                        };
                    }
                };
                // Initiate the GraphQL call
                newEntry.response.refresh();
            };
            // Get or create a cache entry using the factory function
            var newEntry = _this.GetOrCreateCacheEntry(queryAndVariablesString, newEntryFactory, cacheMode === "no-cache");
            // If there are no subscribers and the cache mode is "cache-and-network" or the cache entry has expired, refresh the cache entry
            // (note: 'no-cache' would have already created a new request)
            // But for "cache-first" mode, or subscribers to a request whose entry has not expired, does not need a refresh
            if (newEntry.subscribers.length === 0 && (cacheMode === "cache-and-network" || newEntry.expires < Date.now())) {
                newEntry.response.refresh();
            }
            // Return the cache entry's response object as an IQueryResponse
            return newEntry.response;
        };
        this.ExecuteSubscription = function (request, onData, onClose) {
            _this.activeSubscriptions += 1;
            var subscriptionId = "1";
            // set up abort
            var aborted = false;
            var doAbort = null;
            var abortPromise = new Promise(function (doAbort2) {
                doAbort = doAbort2;
            });
            // abort will set aborted and call onClose
            abortPromise.then(function () {
                _this.activeSubscriptions -= 1;
                aborted = true;
                onClose();
            });
            // set up data push
            var doData = function (data) {
                if (!aborted)
                    onData(data);
            };
            // set up error close
            var doError = function (error) {
                if (aborted)
                    return;
                var queryRet = {
                    networkError: true,
                    errors: [{ message: (typeof (error) === "string") ? error : 'Error generating websocket connection payload' }],
                    extensions: {
                        underlyingError: error,
                    },
                    size: 1000,
                };
                onData(queryRet);
                doAbort();
            };
            // set up connection promise
            var connectionPromise = new Promise(function (resolveConnection, rejectConnection) {
                var connectionResolved = false;
                abortPromise.then(function () {
                    if (!connectionResolved) {
                        connectionResolved = true;
                        rejectConnection();
                    }
                });
                // attempt to generate the connection payload
                var payloadPromise = _this.generatePayload ? Promise.resolve(_this.generatePayload()) : Promise.resolve(undefined);
                payloadPromise.then(function (payload) {
                    if (aborted)
                        return;
                    // connect to the websocket endpoint
                    var webSocket = new WebSocket(_this.webSocketUrl, "graphql-transport-ws");
                    // set up abort/close
                    abortPromise.then(function () {
                        webSocket.close();
                    });
                    // set up state machine
                    var state = "opening";
                    // when connection is opened, send connection init message
                    webSocket.onopen = function () {
                        if (aborted)
                            return;
                        var message = {
                            type: 'connection_init',
                            payload: payload,
                        };
                        webSocket.send(JSON.stringify(message));
                    };
                    // when message received, process it
                    webSocket.onmessage = function (ev) {
                        // parse the incoming message
                        if (aborted)
                            return;
                        if (typeof ev.data !== "string")
                            doError("WebSocket data is not string");
                        var message;
                        try {
                            message = JSON.parse(ev.data);
                        }
                        catch (_a) {
                            doError("WebSocket message is not valid JSON");
                        }
                        if (message === null)
                            doError("WebSocket message is null");
                        // process message based on state machine
                        if (message.type === "ping") {
                            webSocket.send(JSON.stringify({ type: "pong" }));
                        }
                        else if (state === "opening") {
                            if (message.type !== "connection_ack")
                                doError("Invalid connection response from WebSocket server");
                            else {
                                state = "connected";
                                if (!connectionResolved) {
                                    connectionResolved = true;
                                    resolveConnection();
                                }
                                webSocket.send(JSON.stringify({
                                    id: subscriptionId,
                                    type: "subscribe",
                                    payload: request,
                                }));
                            }
                        }
                        else if (state === "connected") {
                            if (message.type === "next") {
                                if (!message.payload || (!message.payload.data && !message.payload.errors) || message.id !== subscriptionId) {
                                    doError("Invalid payload for 'next' packet");
                                }
                                else {
                                    doData({
                                        networkError: false,
                                        size: ev.data.length,
                                        data: message.payload.data,
                                        errors: message.payload.errors,
                                        extensions: message.payload.extensions,
                                    });
                                }
                            }
                            else if (message.type === "error") {
                                if (!message.payload || message.id !== subscriptionId)
                                    doError("Invalid payload for 'error' packet");
                                doData({
                                    networkError: false,
                                    size: ev.data.length,
                                    errors: message.payload,
                                });
                                doAbort();
                            }
                            else if (message.type === "complete") {
                                if (message.id !== subscriptionId)
                                    doError("Invalid payload for 'complete' packet");
                                doAbort();
                            }
                        }
                    };
                    // when websocket closed, notify caller (only raises error if not closed from this end)
                    webSocket.onclose = function () {
                        doError("WebSocket connection unexpectedly closed");
                    };
                }, 
                // when failed to generate payload, notify caller
                function (error) {
                    doError(error);
                });
            });
            return {
                connected: connectionPromise,
                abort: doAbort,
            };
        };
        this.RefreshAll = function (force) {
            // expire all cache entries
            // dump cache entries not in use
            // refresh cache entries in use
            _this.ClearCache();
            _this.cache.forEach(function (value) {
                if (value.subscribers.length > 0) {
                    if (force)
                        value.response.forceRefresh();
                    else
                        value.response.refresh();
                }
            });
        };
        this.ClearCache = function () {
            // expire all cache entries
            // dump cache entries not in use
            // do not refresh cache entries in use
            _this.cache.forEach(function (value) {
                value.expires = 0;
            });
            _this.AllocateCacheSize(_this.maxCacheSize);
        };
        this.ResetStore = function () {
            // expire all cache entries not in use
            // dump cache entries not in use
            _this.ClearCache();
            // refresh all cache entries in use
            _this.cache.forEach(function (value) {
                value.response.clearAndRefresh();
            });
        };
        this.GetOrCreateCacheEntry = function (queryAndVariablesString, newEntryFactory, noCache) {
            // this always adds the new entry even if the lifetime is zero
            var valueFromCache = _this.cache.get(queryAndVariablesString);
            var nowDate = Date.now();
            if (valueFromCache) {
                if (valueFromCache.subscribers.length > 0 || !noCache) {
                    valueFromCache.lastUsed = Date.now();
                    return valueFromCache;
                }
                else {
                    if (_this.cache.delete(queryAndVariablesString))
                        _this.cacheSize -= valueFromCache.size;
                }
            }
            var newEntry = {
                queryAndVariablesString: queryAndVariablesString,
                expires: 0,
                lastUsed: nowDate,
                response: null,
                subscribers: [],
                size: 1000,
            };
            newEntryFactory(newEntry);
            _this.AllocateCacheSize(newEntry.size);
            newEntry.queryAndVariablesString = queryAndVariablesString;
            newEntry.lastUsed = nowDate;
            _this.cacheSize += newEntry.size;
            _this.cache.set(queryAndVariablesString, newEntry);
            return newEntry;
        };
        this.SetCacheEntrySize = function (cacheEntry, newSize) {
            var entry = _this.cache.get(cacheEntry.queryAndVariablesString);
            if (entry && entry === cacheEntry) {
                _this.cacheSize -= entry.size;
                _this.AllocateCacheSize(newSize, entry);
                _this.cacheSize += entry.size;
            }
            cacheEntry.size = newSize;
        };
        this.AllocateCacheSize = function (newSize, exclude) {
            //check if we need more space
            var needBytes = (_this.cacheSize + newSize) - _this.maxCacheSize;
            if (needBytes <= 0) {
                return;
            }
            //delete expired entries
            var nowDate = Date.now();
            var entriesToRemove = [];
            _this.cache.forEach(function (value) {
                if (value.subscribers.length === 0 && value.expires < nowDate) {
                    entriesToRemove.push(value);
                }
            });
            entriesToRemove.forEach(function (value) {
                if (_this.cache.delete(value.queryAndVariablesString)) {
                    _this.cacheSize -= value.size;
                }
            });
            var _loop_1 = function () {
                var needBytes_1 = (_this.cacheSize + newSize) - _this.maxCacheSize;
                if (needBytes_1 <= 0)
                    return "break";
                var oldestEntry;
                _this.cache.forEach(function (value) {
                    if (value.subscribers.length === 0 && value !== exclude) {
                        if (!oldestEntry || value.lastUsed < oldestEntry.lastUsed)
                            oldestEntry = value;
                    }
                });
                if (oldestEntry) {
                    if (_this.cache.delete(oldestEntry.queryAndVariablesString)) {
                        _this.cacheSize -= oldestEntry.size;
                    }
                    oldestEntry = null;
                }
                else {
                    return "break";
                }
            };
            //deleted oldest entry
            do {
                var state_1 = _loop_1();
                if (state_1 === "break")
                    break;
            } while (true);
        };
        this.url = configuration.url;
        this.webSocketUrl = configuration.webSocketUrl || configuration.url;
        this.transformRequest = configuration.transformRequest;
        this.defaultCachePolicy = configuration.defaultFetchPolicy || "cache-first";
        this.defaultCacheTime = configuration.defaultCacheTime !== undefined ? configuration.defaultCacheTime : (24 /* hours */ * 60 /* minutes */ * 60 /* seconds */ * 1000 /* milliseconds */);
        this.generatePayload = configuration.generatePayload;
        this.cache = new Map();
        this.cacheSize = 0;
        this.maxCacheSize = configuration.maxCacheSize || (1024 * 1024 * 20); //20MB
        this.pendingRequests = 0;
        this.activeSubscriptions = 0;
        this.asForm = configuration.asForm;
    }
    return GraphQLClient;
}());
export default GraphQLClient;
